import React, { useState, useEffect } from "react";
import { Storage } from "aws-amplify";
import {
  Row,
  Col,
  Spin,
  Tooltip,
  Card,
  Tag,
  Popconfirm,
  Typography,
  message,
} from "antd";
/* UNUSED: isEmpty - only used in commented processImages function */
// import { isEmpty } from "lodash";
import PaginationTable from "../../../shared/PaginationTable";

// Global Styling
import {
  TableColumnTitle,
  PrimaryTableRowText,
} from "../../../shared/globalStyling/styledText";
import "./../../Creatives/library/creativestyle.css";
import Button from "../../../shared/globalStyling/Button";
import moment from "moment";
import SearchBar from "../../Creatives/library/SearchBar";
import { Link } from "react-router-dom";
import MediaFetcher from "../../../ux/Creatives/library/MediaFetcher";
import CreativePreviewModal from "./CreativePreviewModal";
import { performCreativeSearch } from "../../Creatives/utils/mediaUtils";
import { multiImageZipKey } from "../../../../core/utils/dataloading/multiImageZipKey";
const { Title } = Typography;

// Helper function to get unique medias (moved outside component to avoid hooks issues)
const getUniqueMedias = (medias) => {
  return Array.from(
    new Map(medias.map(media => [media.key, media])).values()
  );
};

// Video Thumbnail Component with custom video handling
const VideoThumbnail = ({ media, size }) => {
  const [videoUrl, setVideoUrl] = React.useState(null);

  React.useEffect(() => {
    let isMounted = true;

    const fetchVideoUrl = async () => {
      try {
        const url = await Storage.get(media.key, { expires: 600000 });
        if (isMounted) {
          setVideoUrl(url);
        }
      } catch (error) {
        if (isMounted) {
          console.error("Error fetching video:", error);
        }
      }
    };

    if (media.key) {
      fetchVideoUrl();
    }

    return () => {
      isMounted = false;
    };
  }, [media.key]);

  return (
    <div style={{
      width: "100%",
      height: "100%",
      position: "relative",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "#f5f5f5",
    }}>
      {videoUrl && (
        <video
          src={videoUrl}
          style={{
            width: "100%",
            height: size,
            objectFit: "contain",
            pointerEvents: "none"
          }}
          muted
          preload="metadata"
        />
      )}
      {/* Play overlay icon */}
      <div style={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: window.innerWidth < 480 ? "20px" : "28px",
        height: window.innerWidth < 480 ? "20px" : "28px",
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "white",
        fontSize: window.innerWidth < 480 ? "10px" : "14px",
        pointerEvents: "none",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)"
      }}>
        ▶
      </div>
    </div>
  );
};

const AdVideosGroupTable = ({
  adVideosGroups,
  /* UNUSED: updateOrgMedia - prop not used since no edit functionality */
  // updateOrgMedia,
  amplify,
  selectedOrg,
  onDeleteAdgroup,
  currentPage: propCurrentPage,
  onPageChange,
}) => {
  const [ids, setIds] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(propCurrentPage || 1);
  const [isLoading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [filteredAdGroups, setFilteredAdGroups] = useState(Array.isArray(adVideosGroups) ? adVideosGroups : []);
  const [searchText, setSearchText] = useState("");
  const [previewImages, setPreviewImages] = useState([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewStartIndex, setPreviewStartIndex] = useState(0);
  const [modalKey, setModalKey] = useState(0);

  // Sync currentPage with prop
  useEffect(() => {
    if (propCurrentPage && propCurrentPage !== currentPage) {
      setCurrentPage(propCurrentPage);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propCurrentPage]);

  useEffect(() => {
    // Apply base filters first
    const safeAdVideosGroups = Array.isArray(adVideosGroups) ? adVideosGroups : [];
    const baseFiltered = safeAdVideosGroups
      .filter(
        adGroup =>
          adGroup.org.length === 1 ||
          (selectedOrg &&
            selectedOrg.parentOrg &&
            selectedOrg.parentOrg.length > 0)
      )
      .filter(
        adGroup =>
          !(
            adGroup.hasOwnProperty("isAmplifyDefault") &&
            adGroup.isAmplifyDefault === true &&
            !amplify
          )
      )
      .filter(item => Array.isArray(item.medias) && item.medias.length)
      .filter(item => item.medias.some(media => media.fileType === "MP4"));

    // Apply search filter if search text exists
    const searchFiltered = performCreativeSearch(baseFiltered, searchText, 'adGroup');

    setFilteredAdGroups(searchFiltered);
  }, [searchText, adVideosGroups, amplify, selectedOrg]);

  // Separate effect to handle selection validation when data changes
  useEffect(() => {
    // Ensure ids is always an array
    if (!Array.isArray(ids)) {
      setIds([]);
      setSelectedRows([]);
      return;
    }
    
    if (ids.length > 0 && filteredAdGroups.length > 0) {
      const validIds = ids.filter(id => 
        filteredAdGroups.some(adGroup => adGroup.id === id)
      );
      if (validIds.length !== ids.length) {
        setIds(validIds);
        setSelectedRows(prev => prev.filter(row => validIds.includes(row.id)));
      }
    }
  }, [filteredAdGroups, ids]);

  const handleSearch = value => {
    setSearchText(value);
  };

  const handleImageClick = (clickedMedia, allImages) => {
    const clickedIndex = allImages.findIndex(img => img.key === clickedMedia.key);
    setPreviewImages(allImages);
    setPreviewStartIndex(clickedIndex);
    setPreviewVisible(true);
    setModalKey(prev => prev + 1); // Force modal recreation
  };

  const handleDownloadAdUnits = async () => {
    if (!ids || ids.length === 0) {
      return message.warning("Please select video ad groups to download");
    }

    if (!selectedRows || selectedRows.length === 0) {
      return message.warning("Please select video ad groups to download");
    }

    // Filter for video media only
    const allMediaKeys = selectedRows
      .flatMap(adGroup => adGroup.medias || [])
      .filter(media => media.fileType === 'MP4' || media.type === 'VIDEO')
      .map(media => media.key)
      .filter(key => key); // Remove any undefined/null keys

    if (allMediaKeys.length === 0) {
      return message.warning("No video creatives found to download. Please ensure the selected ad groups contain video files.");
    }

    try {
      message.loading("Preparing video download...", 0);
      const fileName = `Video_AdGroups_${selectedRows.length}_groups_${allMediaKeys.length}_videos`;
      const results = await multiImageZipKey(fileName, allMediaKeys);
      
      message.destroy();
      
      if (results.successfulDownloads > 0) {
        message.success(
          `Successfully prepared download for ${results.successfulDownloads} video(s)`
        );
      }
      
      if (results.failedDownloads > 0) {
        message.warning(
          `Unable to download ${results.failedDownloads} videos due to invalid file keys or access issues.`
        );
      }
      
      if (results.successfulDownloads === 0) {
        message.error("No videos could be downloaded. Please check if the files exist and are accessible.");
      }
    } catch (error) {
      message.destroy();
      message.error("Failed to prepare video download. Please try again.");
      console.error("Download error:", error);
    }
  };

  let columns = [
    {
      title: <TableColumnTitle text={'Video Ad Group Name'} />,
      dataIndex: 'name',
      key: 'name',
      width: 200,
      align: 'left',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (name, record) => {
        return (
          <>
            <Link to={`/creatives/video/groups/${record.id}`}>
              <PrimaryTableRowText text={name} />
            </Link>
            {record.isAmplifyDefault && amplify ? (
              <Tag color="green">Amplify Default</Tag>
            ) : null}
          </>
        );
      },
    },
    {
      title: <TableColumnTitle text={"Ad Units"} />,
      dataIndex: "media",
      key: "adUnits",
      width: 100,
      align: "left",
      render: (_, record) => {
        return record.medias.length;
      },
    },
    {
      title: <TableColumnTitle text={"Gallery"} />,
      dataIndex: "medias",
      key: "medias",
      width: 300,
      align: "left",
      render: (medias, record) => {
        const uniqueMedias = Array.isArray(medias)
          ? Array.from(
              new Map(
                medias.map(media => [media.id || media.key, media])
              ).values()
            )
          : [];

        // Return early if no medias to avoid errors
        if (!uniqueMedias || uniqueMedias.length === 0) {
          return <div>No videos</div>;
        }

        // Responsive video display based on screen size
        const itemCount = uniqueMedias.length;

        const getMaxVisible = () => {
          if (window.innerWidth < 480) return 2; // Mobile: 2 videos + count
          if (window.innerWidth < 768) return 3; // Small tablet: 3 videos + count
          return 4; // Desktop: 4 videos + count (videos are larger)
        };

        const maxVisible = getMaxVisible();
        const visibleItems = uniqueMedias.slice(0, maxVisible);
        const remainingCount = itemCount - maxVisible;

        // Video thumbnails match individual table sizes
        const videoSize = window.innerWidth < 480 ? "45px" : "60px";
        const gap = window.innerWidth < 480 ? "5px" : "7px";

        return (
          <div style={{
            width: "100%",
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: gap,
          }}>
            {visibleItems.map(media => (
              <div
                key={media.id || media.key}
                onClick={() => handleImageClick(media, uniqueMedias)}
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: videoSize,
                  width: videoSize,
                  minWidth: videoSize,
                  background: "#f5f5f5",
                  border: "1px solid #ddd",
                  overflow: "hidden",
                  flexShrink: 0,
                  cursor: "pointer",
                  transition: "transform 0.2s ease",
                  position: "relative"
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = "scale(1.05)"}
                onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
              >
                <VideoThumbnail
                  media={media}
                  size={videoSize}
                />
              </div>
            ))}
            {remainingCount > 0 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: videoSize,
                  width: videoSize,
                  minWidth: videoSize,
                  background: "#f0f0f0",
                  border: "1px solid #ddd",
                  flexShrink: 0,
                  fontSize: window.innerWidth < 480 ? "9px" : "11px",
                  fontWeight: "bold",
                  color: "#666"
                }}
              >
                +{remainingCount}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: <TableColumnTitle text={'Last Updated'} />,
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 180,
      align: "left",
      sorter: (a, b) => {
        const dateA = moment(a.updatedAt);
        const dateB = moment(b.updatedAt);
        return dateA.isBefore(dateB) ? -1 : dateA.isAfter(dateB) ? 1 : 0;
      },
      render: updatedAt => {
        return (
          <PrimaryTableRowText
            text={moment(updatedAt).format("MM/DD/YYYY hh:mm A")}
          />
        );
      },
    },
  ];

  /* UNUSED: Edit column filtering - no edit column exists in this table
  if (!updateOrgMedia) {
    // Filter edit column if updateOrgMedia is not passed
    columns = columns.filter((column) => column.key !== 'edit');
  }
  */

  const rowSelection = {
    selectedRowKeys: Array.isArray(ids) ? ids : [],
    onChange: (selectedRowKeys, _selectedRows) => {
      const newIds = _selectedRows.map(row => {
        return row.id;
      });
      setIds(newIds);
      setSelectedRows(_selectedRows);
    },
  };

  return (
    <>
      <Row>
        <Col xs={24} xl={24}>
          <Card bodyStyle={{ padding: 10 }}>
            <Row align="middle" className="ant-row-justify-space-between">
              <Col>
                <Title level={4}>Video Ad Groups</Title>
              </Col>
              <Col>
                <SearchBar searchText={searchText} onSearch={handleSearch} />
              </Col>
            </Row>
            <Row align="middle" className="ant-row-justify-space-between">
              <Col xs={24} sm={16} md={16} lg={14}>
                <Row>
                  <Popconfirm
                    placement="topLeft"
                    title={`Are you sure you want to delete the selected ad group? This action cannot be undone.`}
                    onConfirm={async () => {
                      setLoading(true);
                      try {
                        await onDeleteAdgroup(ids);
                        // Clear selection after successful deletion
                        setIds([]);
                        setSelectedRows([]);
                      } catch (error) {
                        console.error("Error deleting ad groups:", error);
                      } finally {
                        setLoading(false);
                      }
                    }}
                    okText="Delete"
                    cancelText="Cancel"
                  >
                    <Tooltip placement="bottom" title={`Delete selected creatives`}>
                      <Button
                        type="default"
                        disabled={!ids || ids.length === 0}
                      >
                        Delete
                      </Button>
                    </Tooltip>
                  </Popconfirm>
                  <Button
                    type="default"
                    disabled={!ids || ids.length === 0}
                    onClick={handleDownloadAdUnits}
                  >
                    Download AD Units
                  </Button>
                </Row>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
      <Spin tip="Loading..." spinning={isLoading}>
        <PaginationTable
          bordered={false}
          footer={
            filteredAdGroups && filteredAdGroups.slice(currentPage * 10 - 10, currentPage * 10)
          }
          columns={columns}
          data={filteredAdGroups}
          scroll={{ x: true, y: false }}
          tableLayout="auto"
          paginationSize={'default'}
          pageSize={pageSize}
          size={'default'}
          total={filteredAdGroups && filteredAdGroups.length}
          current={currentPage}
          rowKey="id"
          rowSelection={rowSelection}
          onChange={(paginationObject) => {
            setPageSize(paginationObject.pageSize);
            setCurrentPage(paginationObject.current);
            if (onPageChange) {
              onPageChange(paginationObject.current);
            }
          }}
        />
      </Spin>

      <CreativePreviewModal
        key={`video-modal-${modalKey}`}
        visible={previewVisible}
        onClose={() => setPreviewVisible(false)}
        images={previewImages}
        startIndex={previewStartIndex}
      />
    </>
  );
};

export default AdVideosGroupTable;
